import { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useSWRConfig } from 'swr';
import useApi from '../hooks/api/useApi';
import { ConfigContext } from '../contexts/ConfigContext';
import { resetPusher } from '../utils/pusher';

export const AuthContext = createContext(null);

const IDLE_MS = 60 * 60 * 1000; // 60 min → auto-logout
const WARN_MS = 55 * 60 * 1000; // 55 min → show warning

export default function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem('accessToken'));
  const { onReset } = useContext(ConfigContext);
  const { mutate: mutateAll } = useSWRConfig();
  const {
    data: meData,
    error,
    isLoading: meLoading,
    refresh: refreshMe
  } = useApi(token ? 'me' : null, {
    revalidateOnMount: true,
    revalidateIfStale: true
  });

  const [user, setUser] = useState(() => {
    const cached = localStorage.getItem('auth_user');
    return cached ? JSON.parse(cached) : null;
  });

  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [countdown, setCountdown] = useState(300);

  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const isWarningRef = useRef(false);
  const throttleRef = useRef(false);

  useEffect(() => {
    if (meData) {
      setUser(meData?.data);
      localStorage.setItem('auth_user', JSON.stringify(meData?.data));
    }
  }, [meData]);

  const clearAllTimers = useCallback(() => {
    clearTimeout(warnTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearInterval(countdownIntervalRef.current);
  }, []);

  const resetTimers = useCallback(() => {
    if (isWarningRef.current) return;

    clearAllTimers();

    warnTimerRef.current = setTimeout(() => {
      isWarningRef.current = true;
      setShowIdleWarning(true);
      setCountdown(300);

      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, WARN_MS);

    logoutTimerRef.current = setTimeout(() => {
      clearAllTimers();
      isWarningRef.current = false;
      localStorage.clear();
      setUser(null);
      setToken(null);
      setShowIdleWarning(false);
      window.location.href = '/login';
    }, IDLE_MS);
  }, [clearAllTimers]);

  useEffect(() => {
    if (!token) {
      clearAllTimers();
      return;
    }

    resetTimers();

    const handleActivity = () => {
      if (throttleRef.current) return;
      throttleRef.current = true;
      setTimeout(() => {
        throttleRef.current = false;
      }, 500);
      resetTimers();
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, handleActivity));

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity));
      clearAllTimers();
    };
  }, [token, resetTimers, clearAllTimers]);

  const login = ({ access_token, refresh_token }) => {
    // Without this, an undefined token persists the STRING "undefined", which is
    // truthy — so the app believes it is signed in, routes to the dashboard, and
    // sends `Bearer undefined` on every request until each one 401s. Worse, the
    // previous session's cached auth_user stays on screen, so the UI shows the
    // wrong person. Same class of bug the refresh_token guard below prevents.
    if (!access_token) {
      throw new Error('login() called without an access token');
    }

    localStorage.setItem('accessToken', access_token);
    // The handoff flow issues a personal access token with no refresh token.
    // Storing undefined/null would persist the string "null", which reads as
    // truthy in axiosClient's `if (!refreshToken)` guard and sends it to the
    // refresh endpoint. Remove the key instead.
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
    } else {
      localStorage.removeItem('refreshToken');
    }
    setToken(access_token);
  };

  const logout = useCallback(() => {
    clearAllTimers();
    isWarningRef.current = false;
    localStorage.clear();
    // Everything SWR is holding answers a question the previous session asked,
    // and the cache is in memory — clearing localStorage does not touch it. On
    // the next sign-in in this same tab, SWR hands back the old answer first and
    // revalidates after, so for a moment the app runs on the last session's
    // data: the previous person's name and permissions if it is someone else,
    // and a stale `onboarding_complete: false` that bounces a merchant who has
    // already finished back to the stepper.
    mutateAll(() => true, undefined, { revalidate: false });
    // Built with the last token and holding subscriptions authorised under it.
    // Now that channels are private, a connection kept across a sign-in would
    // go on authorising as whoever just left.
    resetPusher();
    setUser(null);
    setToken(null);
    setShowIdleWarning(false);
    onReset();
  }, [clearAllTimers, onReset, mutateAll]);

  const stayLoggedIn = useCallback(() => {
    clearInterval(countdownIntervalRef.current);
    isWarningRef.current = false;
    setShowIdleWarning(false);
    setCountdown(300);
    resetTimers();
  }, [resetTimers]);

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60)
      .toString()
      .padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        refreshMe,
        isLoadingUser: token && !meData && !error,
        login,
        logout,
        token,
        meLoading,
        permissions: Array.isArray(user?.permissions) ? user.permissions : [],
        businessType: user?.restaurant?.business_type ?? null,
        modules: user?.modules ?? null,
        // Why a module in `modules` is off: switched off by this restaurant's
        // own admin, rather than withheld by the plan. ModuleRoute needs the
        // difference — one is an upgrade, the other is a switch they own.
        modulesSelfDisabled: user?.modules_self_disabled ?? []
      }}
    >
      {children}

      {showIdleWarning && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: '36px 40px',
              maxWidth: 400,
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 8px 40px rgba(0,0,0,0.18)'
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 12 }}>⏱</div>
            <h5 style={{ fontWeight: 700, marginBottom: 8, color: '#212529' }}>Session Expiring Soon</h5>
            <p style={{ color: '#6c757d', marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
              You&apos;ve been inactive for a while. You&apos;ll be logged out automatically in:
            </p>
            <div
              style={{
                fontFamily: 'monospace',
                fontSize: 38,
                fontWeight: 700,
                color: '#dc3545',
                letterSpacing: 2,
                marginBottom: 24
              }}
            >
              {formatCountdown(countdown)}
            </div>
            <button
              onClick={stayLoggedIn}
              style={{
                background: '#0d6efd',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 28px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Stay Logged In
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}
